import React, { useState, useRef } from "react";
import { 
    Button, 
    TextField, 
    MenuItem, 
    Box, 
    Typography, 
    Paper, 
    Grid, 
    InputAdornment,
    CircularProgress,
    Avatar
} from "@mui/material";
import { 
    UploadCloud, 
    DollarSign, 
    Tag, 
    User, 
    FileText, 
    X,
    Receipt
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  dark: "#22404D",
  azur: "#28708B",
  pearl: "#EEF3F4"
};

function SubirArchivoFinanzas({ adelantoId, actualizar, apiKey }) {
    const [archivo, setArchivo] = useState(null);
    const [rubro, setRubro] = useState("");
    const [detalle, setDetalle] = useState("");
    const [proveedor, setProveedor] = useState("");
    const [monto, setMonto] = useState("");
    const fileInputRef = useRef(null);
    const [cargando, setCargando] = useState(false);

    const cambioElArchivo = (event) => setArchivo(event.target.files[0]);
    const quitarArchivo = () => {
        setArchivo(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const subirArchivo = async () => {
        if (!archivo || !rubro || !monto || !proveedor) {
            toast.warning("Por favor, completa todos los campos obligatorios.");
            return;
        }

        const formData = new FormData();
        formData.append("nombreArchivo", archivo.name);
        formData.append("detalle", detalle);
        formData.append("proveedor", proveedor);
        formData.append("rubro", rubro);
        formData.append("monto", monto);
        formData.append("file", archivo);

        setCargando(true);
        try {
            const response = await fetch(
                `https://pideapi2025.azurewebsites.net/api/Adelanto/agregarItemRendicion?id=${adelantoId}`,
                {
                    method: "PUT",
                    headers: { "apiKey": apiKey },
                    body: formData,
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                toast.error(`Error: ${errorText}`);
                return;
            }

            // Resetear estados
            setArchivo(null);
            setRubro("");
            setDetalle("");
            setProveedor("");
            setMonto("");
            if (fileInputRef.current) fileInputRef.current.value = "";
            
            toast.success("Rendición agregada con éxito");
            actualizar();
        } catch (error) {
            toast.error(`Error: ${error.message}`);
        } finally {
            setCargando(false);
        }
    };

    return (
        <Paper elevation={0} sx={{ 
            p: 4, 
            borderRadius: '32px', 
            border: `1px solid ${PALETTE.sky}`,
            backgroundColor: "#FFFFFF"
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Avatar sx={{ bgcolor: PALETTE.ice, color: PALETTE.azur }}>
                    <Receipt size={24} />
                </Avatar>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: PALETTE.dark, lineHeight: 1.2 }}>
                        Nueva Rendición
                    </Typography>
                    <Typography variant="caption" sx={{ color: PALETTE.azur, fontWeight: 600 }}>
                        Cargar comprobante y detalles del gasto
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Rubro */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        select
                        fullWidth
                        label="Rubro"
                        value={rubro}
                        onChange={(e) => setRubro(e.target.value)}
                        InputProps={{
                            startAdornment: <Tag size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                            sx: { borderRadius: '16px' }
                        }}
                    >
                        <MenuItem value="Gastos Operativos">Gastos Operativos</MenuItem>
                        <MenuItem value="Insumos">Insumos</MenuItem>
                        <MenuItem value="Servicios">Servicios</MenuItem>
                    </TextField>
                </Grid>

                {/* Monto */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Monto"
                        value={monto}
                        onChange={(e) => setMonto(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <DollarSign size={18} color={PALETTE.azur} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: '16px', fontWeight: 700 }
                        }}
                    />
                </Grid>

                {/* Proveedor */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Proveedor"
                        value={proveedor}
                        onChange={(e) => setProveedor(e.target.value)}
                        placeholder="Nombre de la empresa"
                        InputProps={{
                            startAdornment: <User size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                            sx: { borderRadius: '16px' }
                        }}
                    />
                </Grid>

                {/* Detalle */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Detalle / Concepto"
                        value={detalle}
                        onChange={(e) => setDetalle(e.target.value)}
                        placeholder="Descripción breve"
                        InputProps={{
                            startAdornment: <FileText size={18} style={{ marginRight: 8, color: PALETTE.teal }} />,
                            sx: { borderRadius: '16px' }
                        }}
                    />
                </Grid>

                {/* Subida de Archivo */}
                <Grid item xs={12}>
                    {!archivo ? (
                        <Button
                            component="label"
                            fullWidth
                            sx={{
                                py: 3,
                                borderRadius: '20px',
                                border: `2px dashed ${PALETTE.sky}`,
                                color: PALETTE.azur,
                                textTransform: 'none',
                                transition: '0.3s',
                                '&:hover': { backgroundColor: `${PALETTE.ice}20`, borderColor: PALETTE.aqua }
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <UploadCloud size={32} style={{ marginBottom: 4 }} />
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    Adjuntar Comprobante (PDF, JPG, PNG)
                                </Typography>
                            </Box>
                            <input type="file" hidden ref={fileInputRef} onChange={cambioElArchivo} />
                        </Button>
                    ) : (
                        <Box sx={{ 
                            p: 2, borderRadius: '20px', bgcolor: PALETTE.pearl, 
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            border: `2px solid ${PALETTE.aqua}`
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FileText size={24} color={PALETTE.teal} />
                                <Typography variant="body2" sx={{ fontWeight: 800, color: PALETTE.dark }}>
                                    {archivo.name}
                                </Typography>
                            </Box>
                            <Button onClick={quitarArchivo} sx={{ minWidth: 0, color: PALETTE.dark }}>
                                <X size={20} />
                            </Button>
                        </Box>
                    )}
                </Grid>

                {/* Botón Final */}
                <Grid item xs={12}>
                    <Button
                        onClick={subirArchivo}
                        disabled={cargando}
                        variant="contained"
                        fullWidth
                        sx={{
                            py: 2,
                            borderRadius: '16px',
                            backgroundColor: PALETTE.dark,
                            fontWeight: 800,
                            fontSize: '1rem',
                            textTransform: 'none',
                            boxShadow: `0 10px 20px ${PALETTE.dark}30`,
                            '&:hover': { backgroundColor: PALETTE.teal, transform: 'translateY(-2px)' }
                        }}
                    >
                        {cargando ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Registrar Rendición"
                        )}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
}

export default SubirArchivoFinanzas;